import { motion } from "framer-motion";
import { Phone, RefreshCcw, ChevronDown } from "lucide-react";
import heroImg from "../../assets/rosa_matthew.jpg";

export default function Hero() {
  const scrollToRefill = () => {
    document.querySelector("#refill")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-br from-white via-ez-blue-light to-teal-50 min-h-[75vh] flex items-center"
    >
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-teal-50 opacity-60 translate-x-1/2 -translate-y-1/4 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-blue-50 opacity-50 -translate-x-1/3 translate-y-1/4 blur-3xl" />

      <div className="ez-container relative z-10 w-full py-16 lg:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[65vh]">

          {/* LEFT CONTENT */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="flex flex-col justify-center"
          >
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-ez-teal text-sm font-semibold px-4 py-1.5 rounded-full mb-6 w-fit"
            >
              <span className="w-2 h-2 bg-ez-teal rounded-full animate-pulse" />
              Essex County, New Jersey
            </motion.div>

            <h1 className="font-outfit text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-bold text-ez-text leading-[1.1] mb-4">
              Your{" "}
              <span className="text-ez-teal">Friendly</span>
              <br />
              Neighborhood
              <br />
              Pharmacy
            </h1>

            <p className="text-ez-muted text-sm leading-relaxed mb-6 max-w-lg font-jakarta">
              A trusted, women-owned community pharmacy committed to personalized
              healthcare in Essex County, New Jersey. Genuine medicines, expert care,
              and a welcoming environment — right in your neighborhood.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-refill-btn"
                onClick={scrollToRefill}
                className="flex items-center justify-center gap-2 bg-ez-teal hover:bg-ez-teal-dark text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
              >
                <RefreshCcw size={18} />
                Request a Refill
              </motion.button>

              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-call-btn"
                href="tel:+18625201063"
                className="flex items-center justify-center gap-2 border-2 border-ez-teal text-ez-teal hover:bg-ez-teal hover:text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 text-sm"
              >
                <Phone size={18} />
                Call Us Now
              </motion.a>
            </div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-8 flex gap-8"
            >
              {[
                { value: "6+", label: "Years of Care" },
                { value: "100%", label: "Genuine Medicines" },
                { value: "Free", label: "Home Delivery" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl font-bold font-outfit text-ez-teal">{stat.value}</div>
                  <div className="text-xs text-ez-muted font-jakarta">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* RIGHT IMAGE */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative w-full max-w-xl mx-auto">
              {/* Main image with organic shape */}
              <div className="relative overflow-hidden rounded-[2rem] rounded-tl-[5rem] shadow-2xl">
                <img
                  src={heroImg}
                  alt="Friendly pharmacist assisting a customer"
                  className="w-full max-h-[380px] object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ez-teal/20 to-transparent" />
              </div>

              {/* Floating badge - top left */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.4 }}
                className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
                  <span className="text-xl">💊</span>
                </div>
                <div>
                  <div className="text-xs text-ez-muted">Legacy</div>
                  <div className="font-bold text-ez-text font-outfit text-sm">6+ Years</div>
                </div>
              </motion.div>

              {/* Floating badge - bottom right */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.0, duration: 0.4 }}
                className="absolute -bottom-4 -right-4 bg-ez-teal rounded-2xl shadow-xl px-4 py-3 text-white"
              >
                <div className="text-xs opacity-80">Women-Owned</div>
                <div className="font-bold font-outfit text-sm">Community Pharmacy</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-ez-muted"
      >
        <span className="text-xs font-jakarta">Scroll to explore</span>
        <ChevronDown size={18} />
      </motion.div>
    </section>
  );
}
